import React from 'react';
import { Trash2, Info } from 'lucide-react';

/**
 * AttributeTestingTable (design-time) — presentational. Toolbar actions
 * (Suggest / Add / Save) live on the section header in TestDesignTab.
 *
 * Props:
 *   fields         — [{ id, label, description, attribute_ids, pass_condition, ai_suggested }]
 *   attributes     — [{ id, ref }]  derived from test steps (A, B, C…)
 *   onRename       — (id, label) => void
 *   onDescribe     — (id, description) => void
 *   onDelete       — (id) => void
 *   onScope        — (id, attrId, checked) => void
 *   onPassCondition— (id, value) => void
 */
export default function AttributeFieldMapping({
  fields = [],
  attributes = [],
  onRename,
  onDescribe,
  onDelete,
  onScope,
  onPassCondition,
}) {
  return (
    <div>
      <div className="px-6 py-2 border-b border-gray-100 bg-gray-50">
        <span className="text-xs text-gray-500">
          Specific values extracted from evidence for each sample. AI populates these from the control wording — edit as needed.
        </span>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-600 uppercase tracking-wide">
            <th className="px-6 py-3 text-left" style={{ width: '22%' }}>Field Name</th>
            <th className="px-4 py-3 text-left" style={{ width: '26%' }}>What to Extract</th>
            <th className="px-4 py-3 text-left" style={{ width: '20%' }}>
              <div className="flex items-center gap-1">
                Checked on Attributes
                <span className="relative group/tip">
                  <Info className="h-3 w-3 text-gray-400 cursor-help" />
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-52 bg-gray-800 text-white text-xs rounded px-2 py-1.5 hidden group-hover/tip:block z-10 normal-case font-normal tracking-normal shadow-lg">
                    Which attribute letters require this field to be verified. Leave all unchecked if it applies to every attribute.
                  </span>
                </span>
              </div>
            </th>
            <th className="px-4 py-3 text-left" style={{ width: '28%' }}>
              <div className="flex items-center gap-1">
                Passes when…
                <span className="relative group/tip">
                  <Info className="h-3 w-3 text-gray-400 cursor-help" />
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-56 bg-gray-800 text-white text-xs rounded px-2 py-1.5 hidden group-hover/tip:block z-10 normal-case font-normal tracking-normal shadow-lg">
                    The specific value or rule that confirms this field passes for a given sample. Leave blank for informational fields that are not pass/fail.
                  </span>
                </span>
              </div>
            </th>
            <th className="px-2 py-3" style={{ width: '4%' }}></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {fields.length === 0 && (
            <tr>
              <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-400">
                No fields yet. Use <span className="font-medium">Suggest Fields</span> to let AI generate them from the control wording, or add one manually.
              </td>
            </tr>
          )}
          {fields.map(field => {
            const scopedIds = field.attribute_ids || [];
            const appliesToAll = scopedIds.length === 0;
            return (
              <tr key={field.id} className="hover:bg-gray-50 transition-colors group align-top">
                {/* Field Name */}
                <td className="px-6 py-3">
                  <input
                    type="text"
                    value={field.label}
                    onChange={e => onRename(field.id, e.target.value)}
                    placeholder="Field name"
                    className="w-full font-medium text-gray-800 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent px-0 py-0.5"
                  />
                  {field.ai_suggested && (
                    <span className="text-[10px] text-purple-400 mt-0.5 block">AI suggested</span>
                  )}
                </td>

                {/* What to Extract */}
                <td className="px-4 py-3">
                  <textarea
                    rows={2}
                    value={field.description || ''}
                    onChange={e => onDescribe(field.id, e.target.value)}
                    placeholder="What exactly to read from the evidence document…"
                    className="w-full text-sm text-gray-600 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none resize-y bg-transparent px-0 py-0.5"
                  />
                </td>

                {/* Checked on attributes */}
                <td className="px-4 py-3">
                  {attributes.length === 0 ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">All attributes</span>
                  ) : (
                    <div className="flex flex-wrap gap-1 items-center">
                      {attributes.map(attr => {
                        const checked = scopedIds.includes(attr.id);
                        return (
                          <label key={attr.id} className="inline-flex items-center gap-1 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => onScope(field.id, attr.id, e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3 w-3"
                            />
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${checked ? 'bg-blue-100 text-blue-700' : 'text-gray-400'}`}>
                              {attr.ref}
                            </span>
                          </label>
                        );
                      })}
                      {appliesToAll && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">All attributes</span>
                      )}
                    </div>
                  )}
                </td>

                {/* Passes when… */}
                <td className="px-4 py-3">
                  <textarea
                    rows={2}
                    value={field.pass_condition || ''}
                    onChange={e => onPassCondition(field.id, e.target.value)}
                    placeholder={'e.g. "Must equal \'Approved\'" or "Must be ≤ invoice date" or "Must not be blank"'}
                    className="w-full text-sm text-gray-600 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none resize-y bg-transparent px-0 py-0.5"
                  />
                </td>

                {/* Delete */}
                <td className="px-2 py-3">
                  <button
                    type="button"
                    onClick={() => onDelete(field.id)}
                    className="text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove field"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
